/* eslint-env browser */
import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DropzoneArea } from 'react-mui-dropzone';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import DialogView from './DialogView';
import { useTranslation } from './LocalizationProvider';
import { useGlobalStyles } from '../util/classes';
import CameraDialog from '../../install/CameraDialog';

const previewIcon = () => <DescriptionIcon />;

const useStyles = makeStyles((theme) => ({
  fileInput: {
    display: 'none',
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  dropzone: {
    minHeight: 150,
    padding: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  preview: {
    maxWidth: '100%',
    maxHeight: 200,
    objectFit: 'contain',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  processingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  resultContainer: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  resultLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
}));

const DocumentUploadDialog = ({ open, deviceId, handleClose }) => {
  const t = useTranslation();
  const classes = useStyles();
  const globalClasses = useGlobalStyles();

  const [uploadMethod, setUploadMethod] = useState('camera');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const resetState = () => {
    setPreviewImage(null);
    setFile(null);
    setError(null);
    setResult(null);
    setLoading(false);
  };

  const handleUploadMethodChange = (event) => {
    setUploadMethod(event.target.value);
    resetState();
  };

  const handleFileChange = (files) => {
    if (files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);

      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleCameraOpen = () => {
    setCameraOpen(true);
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
  };

  const handleCameraCapture = async (field, imageData) => {
    setPreviewImage(imageData);

    // Convert base64 to File object
    const response = await fetch(imageData);
    const blob = await response.blob();
    setFile(new File([blob], 'document.png', { type: 'image/png' }));

    setCameraOpen(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t('errorNoFileSelected'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Using multipart/form-data for file upload
      const formData = new FormData();

      // Add the file with the field name 'file' to match backend @FormDataParam("file")
      formData.append('file', file);

      // Add deviceId to form data
      formData.append('deviceId', deviceId);

      // Send to API using FormData (multipart/form-data)
      const response = await fetch('/api/devices/document', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await response.text() || t('errorProcessing'));
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || t('errorProcessing'));
    } finally {
      setLoading(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <Box className={classes.resultContainer}>
        <Typography variant="h6" gutterBottom>
          {t('documentProcessingResult')}
        </Typography>

        {/* Device Info */}
        {result.name && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('sharedName')}
              :
            </Typography>
            <Typography>{result.name}</Typography>
          </Box>
        )}

        {/* Vehicle Info */}
        {result.vehicleMake && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('vehicleMake')}
              :
            </Typography>
            <Typography>{result.vehicleMake}</Typography>
          </Box>
        )}

        {result.vehicleModel && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('vehicleModel')}
              :
            </Typography>
            <Typography>{result.vehicleModel}</Typography>
          </Box>
        )}

        {result.vehicleYear && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('vehicleYear')}
              :
            </Typography>
            <Typography>{result.vehicleYear}</Typography>
          </Box>
        )}

        {result.vin && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('vehicleVIN')}
              :
            </Typography>
            <Typography>{result.vin}</Typography>
          </Box>
        )}

        {/* Customer Info */}
        {result.contact && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('deviceContact')}
              :
            </Typography>
            <Typography>{result.contact}</Typography>
          </Box>
        )}

        {result.phone && (
          <Box mb={1}>
            <Typography className={classes.resultLabel}>
              {t('sharedPhone')}
              :
            </Typography>
            <Typography>{result.phone}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <DialogView
        open={open}
        handleClose={handleClose}
        dialogTitle={t('documentUploadTitle')}
        content={(
          <Box className={classes.dialogContent}>
            <Table size="small">
              <TableBody>
                {/* Upload Method Selection Row */}
                <TableRow>
                  <TableCell className={globalClasses.cell}>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        row
                        value={uploadMethod}
                        onChange={handleUploadMethodChange}
                      >
                        <FormControlLabel
                          value="camera"
                          control={<Radio />}
                          label={t('documentUseCamera')}
                        />
                        <FormControlLabel
                          value="upload"
                          control={<Radio />}
                          label={t('documentUploadFile')}
                        />
                      </RadioGroup>
                    </FormControl>
                  </TableCell>
                </TableRow>

                {/* Upload Method Content Row */}
                <TableRow>
                  <TableCell className={globalClasses.cell}>
                    {uploadMethod === 'camera' ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        onClick={handleCameraOpen}
                      >
                        {t('documentTakePhoto')}
                      </Button>
                    ) : (
                      <DropzoneArea
                        acceptedFiles={['image/*', 'application/pdf']}
                        dropzoneText={t('documentDropzoneText')}
                        filesLimit={1}
                        onChange={handleFileChange}
                        maxFileSize={5000000}
                        showAlerts={false}
                        showPreviews={false}
                        showPreviewsInDropzone={false}
                        useChipsForPreview={false}
                        getPreviewIcon={previewIcon}
                      />
                    )}
                  </TableCell>
                </TableRow>

                {/* Preview Image Row */}
                {previewImage && (
                  <TableRow>
                    <TableCell className={globalClasses.cell}>
                      <Box className={classes.previewContainer}>
                        <img
                          src={previewImage}
                          alt={t('documentPreview')}
                          className={classes.preview}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Error Message Row */}
                {error && (
                  <TableRow>
                    <TableCell className={globalClasses.cell}>
                      <Typography color="error" style={{ marginTop: 16 }}>
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Processing Button Row */}
                {!loading && !result && file && (
                  <TableRow>
                    <TableCell className={globalClasses.cell}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        className={classes.uploadButton}
                        startIcon={<UploadFileIcon />}
                      >
                        {t('documentProcess')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                {/* Loading Indicator Row */}
                {loading && (
                  <TableRow>
                    <TableCell className={globalClasses.cell}>
                      <Box className={classes.processingContainer}>
                        <CircularProgress size={36} />
                        <Typography style={{ marginTop: 16 }}>
                          {t('documentProcessing')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Results Row */}
                {result && (
                  <>
                    <TableRow>
                      <TableCell className={globalClasses.cell}>
                        {renderResult()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={globalClasses.cell}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            // Clone the result to ensure we're passing a clean object
                            const resultCopy = { ...result };
                            handleClose(resultCopy);
                          }}
                          className={classes.uploadButton}
                        >
                          {t('sharedAccept')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      />
      <CameraDialog
        open={cameraOpen}
        onClose={handleCameraClose}
        field="document"
        imageOnly
        onCapture={handleCameraCapture}
      />
    </>
  );
};

export default DocumentUploadDialog;
